/**
 * Searchable multi-select for required platforms.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Field } from "@ui/forms";
import {
  filterDraftPlatforms, formatDraftPlatforms, parseDraftPlatforms,
  type DraftPlatform,
} from "./draftForm";

const GROUP_LABEL = { social: "Social", forum: "Forums" } as const;

export function PlatformSelect({
  value, onChange, error, required, hint,
}: {
  value: string;
  onChange: (next: string) => void;
  error?: string;
  required?: boolean;
  hint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panel, setPanel] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selected = parseDraftPlatforms(value);
  const summary = selected.length ? formatDraftPlatforms(selected) : "Search platforms";
  const matches = useMemo(() => filterDraftPlatforms(query), [query]);

  useEffect(() => {
    if (!open) {
      setPanel(null);
      return;
    }
    setQuery("");
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    function place() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const openUp = below < 240 && above > below;
      const maxHeight = Math.min(360, Math.max(160, openUp ? above : below));
      setPanel({
        top: openUp ? Math.max(8, r.top - 6 - maxHeight) : r.bottom + 6,
        left: r.left,
        width: r.width,
        maxHeight,
      });
    }
    place();
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    document.addEventListener("scroll", place, true);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      document.removeEventListener("scroll", place, true);
    };
  }, [open]);

  function toggle(id: string) {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    onChange(formatDraftPlatforms(next));
  }

  const searching = query.trim().length > 0;
  const groups: { key: "social" | "forum"; rows: DraftPlatform[] }[] = searching
    ? [{ key: "social", rows: matches }]
    : [
      { key: "social", rows: matches.filter((p) => p.group === "social") },
      { key: "forum", rows: matches.filter((p) => p.group === "forum") },
    ];

  return (
    <Field
      label="Required platforms"
      required={Boolean(required)}
      error={error}
      {...(hint ? { hint } : {})}
    >
      {({ id, describedBy, invalid }) => (
        <div className="cm-platform-drop" ref={rootRef} data-invalid={invalid || undefined} data-open={open || undefined}>
          <button
            type="button"
            id={id}
            ref={triggerRef}
            className="cm-platform-drop-trigger"
            aria-expanded={open}
            aria-controls={`${id}-list`}
            aria-describedby={describedBy}
            onClick={() => setOpen((v) => !v)}
          >
            {summary}
          </button>
          {open && panel ? (
            <div
              className="cm-platform-drop-list"
              id={`${id}-list`}
              role="group"
              aria-label="Required platforms"
              style={{ top: panel.top, left: panel.left, width: panel.width, maxHeight: panel.maxHeight }}
            >
              <input
                ref={searchRef}
                type="search"
                className="cm-platform-drop-search"
                placeholder="Search platforms and forums"
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
                aria-label="Search platforms and forums"
              />
              <div className="cm-platform-drop-options">
                {matches.length === 0 ? (
                  <p className="cm-platform-drop-empty">No platforms match “{query.trim()}”.</p>
                ) : groups.map((g) => (
                  g.rows.length === 0 ? null : (
                    <div key={g.key}>
                      {!searching ? (
                        <p className="cm-platform-drop-group">{GROUP_LABEL[g.key]}</p>
                      ) : null}
                      {g.rows.map((p) => (
                        <label key={p.id}>
                          <input
                            type="checkbox"
                            checked={selected.includes(p.id)}
                            onChange={() => toggle(p.id)}
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  )
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Field>
  );
}
